import { Injectable } from '@angular/core';
import { participantPages } from '@app/pages/participant/participants.routes';
import { INavTreeNode } from '@app/workflow/components/navigator/nav-tree/interfaces';
import { SessionItemData } from '@app/workflow/services/session-item-data';
import { WizardPageTreeNode } from '@workflow/components/navigator/nav-tree/nav-tree-nodes';
import { trimStart } from 'lodash';


/**
 * Nächsten Nachbarn suchen. Liefert den nächsten Nachbarn des Knoten oder eines Elten-Knotens
 * @param current Start-Knoten
 * @param stop Optionale Stop-Bedingung. Die Iteration bricht ab, wenn stop erreicht wird
 */
function nextNeighbour(current: INavTreeNode, stop?: INavTreeNode): INavTreeNode {
	while (current && current !== stop) {
		if (current.nextSibling) {
			current = current.nextSibling;
			break;
		}
		current = current.parent;
	}
	return current;
}

/**
 * Vorherigen Nachbarn suchen. Liefert den vorherigen Nachbarn des Knoten oder eines Elten-Knotens
 * @param current Start-Knoten
 * @param stop Optionale Stop-Bedingung. Die Iteration bricht ab, wenn stop erreicht wird
 */
function prevNeighbour(current: INavTreeNode, stop?: INavTreeNode): INavTreeNode {
	while (current && current !== stop) {
		if (current.prevSibling) {
			current = current.prevSibling;
			break;
		}
		current = current.parent;
	}
	return current;
}

/**
 * Vorwärts-Iteration durch den Baum
 * @param current Start-Knoten
 * @param data Optionales SessionItemData zur isEnabled-Prüfung
 * @param stop Optionale Stop-Bedingung. Die Iteration bricht ab, wenn stop erreicht wird
 */
export function* forward_iterator(current: INavTreeNode, data?: SessionItemData, stop?: INavTreeNode): Generator<INavTreeNode> {
	while (current && current !== stop) {
		if (!current.firstChild) {
			yield current;
		}

		if (current.firstChild && (!data || current.isEnabled(data))) {
			current = current.firstChild;
			continue;
		}

		current = nextNeighbour(current);
	}
}

/**
 * Rückwärts-Iteration durch den Baum
 * @param current Start-Knoten
 * @param data Optionales SessionItemData zur isEnabled-Prüfung
 * @param stop Optionale Stop-Bedingung. Die Iteration bricht ab, wenn stop erreicht wird
 */
export function* reverse_iterator(current: INavTreeNode, data?: SessionItemData, stop?: INavTreeNode): Generator<INavTreeNode> {
	while (current && current !== stop) {
		if (!current.lastChild) {
			yield current;
		}

		if (current.lastChild && (!data || current.isEnabled(data))) {
			current = current.lastChild;
			continue;
		}

		current = prevNeighbour(current, stop);
	}
}

export type FindNodeOptions = {
	/**
	 * findNode liefert auch Zweige (nicht-Blattknoten), wenn die URL exakt übereinstimmt
	 */
	allowBranch?: boolean;
};

export type FindValidOptions = {
	/**
	 * Workflowschritt ist abgeschlossen
	 */
	finished?: boolean;
	/**
	 * Springt zum ersten unvollständigen Knoten
	 * Option wird ignoriert, wenn "finished" gesetzt ist
	 */
	firstIncomplete?: boolean;
};

@Injectable({
	providedIn: 'root'
})
export class NavTreeNavigator {
	/**
	 * Blatt-Knoten per URL-Prefix suchen. Die Funktion liefert auch deaktivierte Knoten.
	 *
	 * Die Suche bricht ab, wenn ein Blatt-Knoten gefunden wurde, dessen URL-Prefixe mit der URL übereinstimmen
	 *
	 * @param current Start-Knoten
	 * @param url URL
	 */
	findNode(current: INavTreeNode, url: string, options?: FindNodeOptions): INavTreeNode {
		while (current) {
			// Stimmt einer der Prefixe?
			const prefixMatch = current.urlPrefixes.some(urlPrefix => url.startsWith(urlPrefix + '/'));
			const exactMatch = current.urlPrefixes.some(urlPrefix => url === urlPrefix);
			if (prefixMatch || exactMatch) {
				// Blatt-Knoten
				if (!current.firstChild) {
					return current;
				}
				if (options?.allowBranch && exactMatch) {
					return current;
				}
				// Eine Ebene tiefer weitersuchen
				current = current.firstChild;
				continue;
			}

			// Prefix stimmt nicht, mit dem nächsten Knoten weitermachen
			current = nextNeighbour(current);
		}

		return null;
	}

	/**
	 * Letzten (Eltern-)Knoten mit isProcedure finden
	 * @param current Start-Knoten
	 */
	getProcedureRoot(current: INavTreeNode): INavTreeNode {
		while (current && current.isProcedure) {
			if (!current.parent || !current.parent.isProcedure) {
				return current;
			}
			current = current.parent;
		}
		return null;
	}

	/**
	 * Nächste gültige Seite
	 *
	 * @param current Start-Knoten
	 * @param data SessionItemData
	 * @param options Optionen
	 */
	nextPage(current: INavTreeNode, data: SessionItemData, options?: { restart?: boolean }): INavTreeNode {
		const restart = options?.restart;
		const procedureRoot = this.getProcedureRoot(current);
		const gotoEpilog = current.isProcedure && current.isProlog && procedureRoot?.isCompleted(data) && !restart;
		const iterator = forward_iterator(current, data);

		for (const node of iterator) {
			if (node === current) {
				continue;
			}
			if (this.isEnabled(node, data)) {
				if (gotoEpilog && !node.isEpilog) {
					// nur innerhalb der Prozedur nach dem Epilog suchen
					if (procedureRoot && procedureRoot === this.getProcedureRoot(node)) {
						continue;
					}
				}
				return node;
			}
		}

		return null;
	}

	/**
	 * Vorherige gültige Seite
	 *
	 * @param current Start-Knoten
	 * @param data SessionItemData
	 */
	prevPage(current: INavTreeNode, data: SessionItemData): INavTreeNode {
		/** oberster Knoten der aktuellen Prozedur */
		let procedureRoot = current.isProcedure && this.getProcedureRoot(current);
		/** Prozeduren müssen zum Anfang der Prozedur zurückgehen */
		let goToStart = current.isProcedure;
		/** Die vorherigen Epilogseiten sind bei goToStart trotzdem zulässig */
		let allowEpilog = goToStart && current.isEpilog;
		/** Rückwärts von der Epilogseite soll auf der letzten Prologseite landen */
		let allowLastProlog = goToStart;
		/** Gefundene Startseite der aktuellen Prozedur */
		let startPage = null;
		for (const node of reverse_iterator(current, data)) {
			if (node === current) {
				continue;
			}
			/** oberster Knoten der Prozedur des Knotens */
			const nodeProcedureRoot = node.isProcedure && this.getProcedureRoot(node);

			// Nicht mehr in der Prozedur, gefundene Startseite ausgeben
			if (startPage && procedureRoot !== nodeProcedureRoot) {
				return startPage;
			}

			// Neue Procedur
			if (node.isProcedure && procedureRoot !== nodeProcedureRoot) {
				procedureRoot = nodeProcedureRoot;
				goToStart = !procedureRoot?.isCompleted(data);
				allowEpilog = false;
				allowLastProlog = false;
			}
			if (this.isEnabled(node, data)) {
				// nur innerhalb der Prozedur nach der Startseite suchen
				if (goToStart && procedureRoot && procedureRoot === nodeProcedureRoot) {
					// Epilogseiten können rückwärts durchlaufen werden
					if (allowEpilog && node.isEpilog) {
						return node;
					}
					// Letzte Prologseite
					if (allowLastProlog && node.isProlog) {
						return node;
					}
					// Gefundene Startseite merken
					startPage = node;
					continue;
				}
				return node;
			}
		}
		return startPage;
	}

	/**
	 * Nächste gültige Fragegruppe
	 * @param currentNode aktueller Knoten
	 * @param data SessionItemData
	 */
	nextGroup(currentNode: INavTreeNode, data: SessionItemData): INavTreeNode {
		while (currentNode && !currentNode.isQuestionGroup) {
			currentNode = currentNode.parent;
		}
		currentNode = currentNode && nextNeighbour(currentNode);
		return currentNode && this.findFirstValid(currentNode, data);
	}

	/**
	 * Prüpft Knoten und Elten, ob sie aktiv sind
	 * @param current Start-Knoten
	 * @param data SessionItemData
	 */
	isEnabled(current: INavTreeNode, data: SessionItemData): boolean {
		if (!current)
			return true;
		return current.isEnabled(data) && this.isEnabled(current.parent, data);
	}

	/**
	 * Gültigen Knoten suchen.
	 * Liefert den Knoten, falls er gültig ist. Sonst wird ein passender gültiger (der nächste) Knoten gesucht.
	 *
	 * @param current Start-Knoten
	 * @param data SessionItemData
	 */
	findValidNode(current: INavTreeNode, data: SessionItemData, options?: { firstIncomplete?: boolean, finished?: boolean }): INavTreeNode {

		// Check if parent nodes are disabled
		while (current && !this.isEnabled(current.parent, data)) {
			current = current.parent;
		}

		if (current && !current.firstChild && current.isEnabled(data)) {
			return current;
		}

		if (current && !current.firstChild) {
			return this.nextPage(current, data);
		}

		while (current) {
			if (current.isEnabled(data)) {
				const validNode = this.findFirstValid(current, data, options);
				if (validNode) {
					return validNode;
				}
			}
			current = nextNeighbour(current);
		}

		return null;
	}

	/**
	 * Nächsten gültigen Knoten suchen
	 * @param current Start-Knoten
	 * @param data SessionItemData
	 */
	private findFirstValid(current: INavTreeNode, data: SessionItemData, options?: { firstIncomplete?: boolean, finished?: boolean }): INavTreeNode {
		const iterator = forward_iterator(current, data);
		let lastComplete = null;
		for (const node of iterator) {
			if (!this.isEnabled(node, data)) {
				continue;
			}
			if (options?.finished && node.isProcedure && !node.isEpilog) {
				continue;
			}

			const procedureRoot = node.isProcedure && this.getProcedureRoot(node);
			const completed = node.isProcedure ? procedureRoot?.isCompleted(data) : node.isCompleted(data);
			if (!options?.finished && options?.firstIncomplete && completed) {
				lastComplete = node;
				continue;
			}
			return node;
		}
		if (options?.firstIncomplete) {
			return lastComplete;
		}
		return null;
	}

	/**
	 * URL zu einem Knoten ermitteln
	 * @param node Knoten
	 * @param workflowId Workflow-Id
	 */
	getUrl(node: INavTreeNode, workflowId: string): string {
		if (!node?.urlPrefixes?.length)
			return undefined;
		return participantPages.workflowProcess.base({wfItemId: workflowId, pageUrl: trimStart(node.urlPrefixes[0], '/')});
	}

	findNextEpilog(start: INavTreeNode, data: SessionItemData): INavTreeNode {
		const iterator = forward_iterator(start, data, start?.parent);
		for (const node of iterator) {
			if (node === start && !node.isEnabled(data)) {
				continue;
			}
			if (node.isEpilog) {
				return node;
			}
		}
		return null;
	}

	findPrevProlog(start: INavTreeNode, data: SessionItemData, options?: { getFirstPage?: boolean }): INavTreeNode {
		// WizardPageTreeNodes für Eingabeseiten befinden sich in Baumhierarchie unter Gruppenprologen
		// Bei individuellen Fragen vertrauen wir darauf, dass wir die Stop-Bedingung nicht brauchen, da individuelle Fragen einen Prolog haben müssen
		const isInputPageNode = !start.isProlog && !start.isEpilog && (start as WizardPageTreeNode).page;
		const parentForProlog = isInputPageNode ? start.parent?.parent: start.parent;

		const iterator = reverse_iterator(start, data, parentForProlog);
		let prolog;
		for (const node of iterator) {
			if (node === start && !node.isEnabled(data)) {
				continue;
			}
			if (node.isProlog) {
				if (options?.getFirstPage){
					prolog = node;
				} else {
					return node;
				}
			} else if (prolog) {
				return prolog;
			}
		}
		return prolog || null;
	}

	areChildrenDisabled(parent: INavTreeNode, data: SessionItemData): boolean {
		const nextNeighbor = nextNeighbour(parent);
		const iterator = forward_iterator(parent, null, nextNeighbor);
		for (const node of iterator) {
			if (!node.isProlog && !node.isEpilog && node.isEnabled(data)) {
				return false;
			}
		}
		return true;
	}
}
